import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import { Box, Divider, IconButton, Stack, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { PanelHeader } from "../../../components/UIToolkit/molecules/PanelHeader";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { ICON_SIZE } from "../../../theme/tokens";
import type { PbjPrediction } from "../../../utils/models";
import { PBJ_ALPHA, PBJ_RADIUS } from "./colors";
import type { PbjDraftControls } from "./pbjDraftControls";
import type { PeriodMode } from "./RecommendationsSidebar";
import { WageOverridesDialog } from "./WageOverridesDialog";

type Props = {
	prediction: PbjPrediction;
	draftControls: PbjDraftControls;
	// Shows "Drag right to add hours, left to cut them" only when some lever can be cut.
	anyCuttable: boolean;
	periodMode: PeriodMode;
	onPeriodModeChange: (next: PeriodMode) => void;
	canSuggest: boolean;
	isSuggesting: boolean;
	onSuggest: () => void;
	onReset: () => void;
};

/**
 * The recommendations panel's title bar: the titled header, then a settings cluster (period
 * toggle + wage gear) divided from the primary "Suggest plan" CTA and its Reset companion. Owns the
 * wage-overrides dialog, which seeds from the draft and recalculates on Save.
 */
export const RecommendationsHeader: FC<Props> = ({
	prediction,
	draftControls,
	anyCuttable,
	periodMode,
	onPeriodModeChange,
	canSuggest,
	isSuggesting,
	onSuggest,
	onReset,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const accent = theme.palette.primary.main;
	const [wageOverridesOpen, setWageOverridesOpen] = useState(false);

	const iconAffordanceSx = {
		width: 30,
		height: 30,
		borderRadius: PBJ_RADIUS.CONTROL,
		color: accent,
		bgcolor: alpha(accent, PBJ_ALPHA.TINT),
		border: "1px solid",
		borderColor: alpha(accent, PBJ_ALPHA.BORDER),
		"&:hover": { bgcolor: alpha(accent, PBJ_ALPHA.TINT + 0.06) },
	} as const;

	return (
		<Box
			sx={{
				py: 1.25,
				px: 2,
				minHeight: 52,
				bgcolor: "background.default",
				borderBottom: "1px solid",
				borderColor: "divider",
				display: "flex",
				alignItems: "center",
			}}
		>
			{/* The settings cluster rides PanelHeader's `endingContent` slot, which right-aligns and wraps
			    below the title on a narrow panel (keeping the title from truncating). */}
			<PanelHeader
				icon={<TipsAndUpdatesOutlinedIcon />}
				title={t("pbj_model.recommendations.title")}
				subtitle={anyCuttable ? t("pbj_model.recommendations.subheader") : undefined}
				endingContent={
					<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
						{/* Period toggle: switches the math tooltip's day denominator between elapsed-to-today
						    and full calendar-quarter. Only meaningful for in-progress quarters. */}
						<SystemFlatSwitch<PeriodMode>
							options={[
								{ label: t("pbj_model.recommendations.period.qtd"), value: "qtd" },
								{ label: t("pbj_model.recommendations.period.quarter"), value: "quarter" },
							]}
							activeIndexOrValue={periodMode}
							onSwitch={(option) => onPeriodModeChange(option.value)}
							delayCallback={0}
							data-testid="pbj-model-recommendations-period-toggle"
						/>
						<InHouseTooltip title={t("pbj_model.recommendations.wage_overrides.tooltip")} arrow>
							<IconButton
								size="small"
								onClick={() => setWageOverridesOpen(true)}
								sx={iconAffordanceSx}
								aria-label={t("pbj_model.recommendations.wage_overrides.tooltip")}
								data-testid="pbj-model-wage-overrides-chip"
							>
								<SettingsIcon sx={{ fontSize: ICON_SIZE.MICRO }} />
							</IconButton>
						</InHouseTooltip>
						<Divider orientation="vertical" flexItem sx={{ my: 0.75 }} />
						<InHouseTooltip title={canSuggest ? "" : t("pbj_model.recommendations.suggest_plan_unavailable")} arrow>
							{/* span wrapper so the tooltip still fires while the button is disabled */}
							<span>
								<SystemButton
									buttonVariant="primary"
									size="small"
									disabled={!canSuggest || isSuggesting}
									loading={isSuggesting}
									onClick={onSuggest}
									leadingIcon={<AutoAwesomeIcon sx={{ fontSize: ICON_SIZE.PILL }} />}
									noFullWidthMobile
									data-testid="pbj-model-suggest-plan"
								>
									{t("pbj_model.recommendations.suggest_plan")}
								</SystemButton>
							</span>
						</InHouseTooltip>
						{/* Reset back to the post-refresh default (sliders + Action Plan re-seeded from the
						    pre-computed suggestion). Secondary affordance beside the primary CTA. */}
						<InHouseTooltip title={t("pbj_model.recommendations.reset_plan")} arrow>
							<IconButton
								size="small"
								onClick={onReset}
								sx={iconAffordanceSx}
								aria-label={t("pbj_model.recommendations.reset_plan")}
								data-testid="pbj-model-recommendations-reset"
							>
								<RestartAltIcon sx={{ fontSize: ICON_SIZE.MICRO }} />
							</IconButton>
						</InHouseTooltip>
					</Stack>
				}
			/>
			<WageOverridesDialog
				open={wageOverridesOpen}
				onClose={() => setWageOverridesOpen(false)}
				prediction={prediction}
				draft={draftControls.draft}
				onDraftChange={draftControls.onDraftChange}
				onCalculate={draftControls.onCalculate}
			/>
		</Box>
	);
};
